/* Wix return and shipping policy markup builder.

   Google reads a store's return policy (since 2024-06-11) and its shipping
   policy (since 2025-11-12) from markup about the business as a whole, and
   Wix's automatic Product markup has neither. This file turns a filled-in
   policy into one JSON-LD block, checks it against Google's required
   properties and Wix's size limits, and draws every part of the result. The
   rules are plain functions, so the tests and the server-rendered first paint
   (wix-policy-markup.cjs) run the same code the browser runs. Nothing here
   fetches, stores or sends what is typed.

   Property names and enumeration values follow Google's return policy and
   shipping policy documentation, as recorded in research sheet R3 (B6),
   checked 2026-10-02. Wix's limits come from its Help Center (R3 A2). */
(function (root, factory) {
  'use strict';
  const shared = (typeof require === 'function' && typeof module === 'object') ? require('./wix-shared.js') : root.WixShared;
  const api = factory(shared || null);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.WixPolicyMarkup = api;
  if (typeof document !== 'undefined' && typeof document.querySelector === 'function') {
    const start = () => api.init(document.querySelector('[data-wpm-lab]'));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (shared) {
  'use strict';

  // ---- Shared helpers (with small stand-ins if wix-shared.js didn't load) ----
  const esc = shared && shared.esc
    ? shared.esc
    : value => String(value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const tidyText = shared && shared.tidyText
    ? shared.tidyText
    : value => String(value == null ? '' : value).replace(/[\u00a0\u2007\u202f]/g, ' ').replace(/[\u200b-\u200d\u2060\ufeff]/g, '').replace(/\s+/g, ' ').trim();

  // ---- Limits ------------------------------------------------------------------
  // Wix: "Structured Data must be less than 7,000 characters to be added", up
  // to 5 markups on a page and 8,000 characters in total on one page.
  const LIMIT = 7000;
  const PAGE_TOTAL = 8000;
  const PER_PAGE = 5;
  // Google: applicableCountry takes up to 50 countries.
  const MAX_RETURN_COUNTRIES = 50;
  // This tool's own limits, said as such where they show. Google sets no
  // maximum return window, so that cap only catches a typo (100 years).
  const MAX_PROFILES = 5;
  const MAX_RETURN_DAYS = 36500;
  const MAX_SHIP_DAYS = 365;

  const SCHEMA = 'https://schema.org/';
  // Wix Help Center pages for the limits above.
  const WIX_ADDING = 'https://support.wix.com/en/article/adding-structured-data-markup-to-your-sites-pages-2546962';
  const WIX_TROUBLESHOOTING = 'https://support.wix.com/en/article/troubleshooting-your-structured-data-markup';
  const OPEN_TAG = '<script type="application/ld+json">';
  const CLOSE_TAG = '</script>';

  const META = Object.freeze({
    path: '/tools/wix-policy-markup',
    name: 'Wix return and shipping policy markup',
    summary: 'Write return and shipping policy markup for Google, check it against Wix’s limits, and see where to paste it.'
  });

  // ---- Google's names --------------------------------------------------------------
  const ORG_TYPES = Object.freeze(['OnlineStore', 'Organization']);
  const RETURN_CATEGORIES = Object.freeze({
    finite: 'MerchantReturnFiniteReturnWindow',
    unlimited: 'MerchantReturnUnlimitedWindow',
    none: 'MerchantReturnNotPermitted'
  });
  const RETURN_MODES = Object.freeze(Object.keys(RETURN_CATEGORIES));
  const RETURN_FEES = Object.freeze(['FreeReturn', 'ReturnFeesCustomerResponsibility', 'ReturnShippingFees']);
  const RETURN_METHODS = Object.freeze(['ReturnByMail', 'ReturnInStore', 'ReturnAtKiosk']);
  const REFUND_TYPES = Object.freeze(['FullRefund', 'ExchangeRefund', 'StoreCreditRefund']);
  const ITEM_CONDITIONS = Object.freeze(['NewCondition', 'UsedCondition', 'RefurbishedCondition']);
  const SHIP_RATES = Object.freeze(['free', 'flat', 'unstated']);
  // Google reads shipping conditions with no destination as worldwide.
  const SHIP_SCOPES = Object.freeze(['countries', 'worldwide']);
  const LAYOUTS = Object.freeze(['pretty', 'line']);

  // Words the form and the read-back use for each value.
  const WORDS = Object.freeze({
    orgType: Object.freeze({ OnlineStore: 'Online store', Organization: 'Organization' }),
    returnMode: Object.freeze({ finite: 'Returns accepted within a set number of days', unlimited: 'Returns accepted with no time limit', none: 'We don’t accept returns' }),
    returnFees: Object.freeze({ '': 'Not stated', FreeReturn: 'Free for the customer', ReturnFeesCustomerResponsibility: 'The customer pays for and arranges it', ReturnShippingFees: 'We charge a set fee' }),
    returnMethods: Object.freeze({ ReturnByMail: 'By mail', ReturnInStore: 'In a store', ReturnAtKiosk: 'At a kiosk' }),
    refundTypes: Object.freeze({ FullRefund: 'Full refund', ExchangeRefund: 'Exchange', StoreCreditRefund: 'Store credit' }),
    itemConditions: Object.freeze({ NewCondition: 'New', UsedCondition: 'Used', RefurbishedCondition: 'Refurbished' }),
    shipRate: Object.freeze({ free: 'Free shipping', flat: 'One flat price', unstated: 'I’d rather not say' })
  });

  // ---- Countries: ISO 3166-1 alpha-2, every officially assigned code ----------------
  const COUNTRY_TABLE = 'AD:Andorra|AE:United Arab Emirates|AF:Afghanistan|AG:Antigua and Barbuda|AI:Anguilla|AL:Albania|AM:Armenia|AO:Angola|AQ:Antarctica|AR:Argentina|AS:American Samoa|AT:Austria|AU:Australia|AW:Aruba|AX:Åland Islands|AZ:Azerbaijan|BA:Bosnia and Herzegovina|BB:Barbados|BD:Bangladesh|BE:Belgium|BF:Burkina Faso|BG:Bulgaria|BH:Bahrain|BI:Burundi|BJ:Benin|BL:Saint Barthélemy|BM:Bermuda|BN:Brunei|BO:Bolivia|BQ:Caribbean Netherlands|BR:Brazil|BS:Bahamas|BT:Bhutan|BV:Bouvet Island|BW:Botswana|BY:Belarus|BZ:Belize|CA:Canada|CC:Cocos (Keeling) Islands|CD:Democratic Republic of the Congo|CF:Central African Republic|CG:Republic of the Congo|CH:Switzerland|CI:Côte d’Ivoire|CK:Cook Islands|CL:Chile|CM:Cameroon|CN:China|CO:Colombia|CR:Costa Rica|CU:Cuba|CV:Cape Verde|CW:Curaçao|CX:Christmas Island|CY:Cyprus|CZ:Czechia|DE:Germany|DJ:Djibouti|DK:Denmark|DM:Dominica|DO:Dominican Republic|DZ:Algeria|EC:Ecuador|EE:Estonia|EG:Egypt|EH:Western Sahara|ER:Eritrea|ES:Spain|ET:Ethiopia|FI:Finland|FJ:Fiji|FK:Falkland Islands|FM:Micronesia|FO:Faroe Islands|FR:France|GA:Gabon|GB:United Kingdom|GD:Grenada|GE:Georgia|GF:French Guiana|GG:Guernsey|GH:Ghana|GI:Gibraltar|GL:Greenland|GM:Gambia|GN:Guinea|GP:Guadeloupe|GQ:Equatorial Guinea|GR:Greece|GS:South Georgia and the South Sandwich Islands|GT:Guatemala|GU:Guam|GW:Guinea-Bissau|GY:Guyana|HK:Hong Kong|HM:Heard Island and McDonald Islands|HN:Honduras|HR:Croatia|HT:Haiti|HU:Hungary|ID:Indonesia|IE:Ireland|IL:Israel|IM:Isle of Man|IN:India|IO:British Indian Ocean Territory|IQ:Iraq|IR:Iran|IS:Iceland|IT:Italy|JE:Jersey|JM:Jamaica|JO:Jordan|JP:Japan|KE:Kenya|KG:Kyrgyzstan|KH:Cambodia|KI:Kiribati|KM:Comoros|KN:Saint Kitts and Nevis|KP:North Korea|KR:South Korea|KW:Kuwait|KY:Cayman Islands|KZ:Kazakhstan|LA:Laos|LB:Lebanon|LC:Saint Lucia|LI:Liechtenstein|LK:Sri Lanka|LR:Liberia|LS:Lesotho|LT:Lithuania|LU:Luxembourg|LV:Latvia|LY:Libya|MA:Morocco|MC:Monaco|MD:Moldova|ME:Montenegro|MF:Saint Martin (French part)|MG:Madagascar|MH:Marshall Islands|MK:North Macedonia|ML:Mali|MM:Myanmar|MN:Mongolia|MO:Macao|MP:Northern Mariana Islands|MQ:Martinique|MR:Mauritania|MS:Montserrat|MT:Malta|MU:Mauritius|MV:Maldives|MW:Malawi|MX:Mexico|MY:Malaysia|MZ:Mozambique|NA:Namibia|NC:New Caledonia|NE:Niger|NF:Norfolk Island|NG:Nigeria|NI:Nicaragua|NL:Netherlands|NO:Norway|NP:Nepal|NR:Nauru|NU:Niue|NZ:New Zealand|OM:Oman|PA:Panama|PE:Peru|PF:French Polynesia|PG:Papua New Guinea|PH:Philippines|PK:Pakistan|PL:Poland|PM:Saint Pierre and Miquelon|PN:Pitcairn Islands|PR:Puerto Rico|PS:Palestine|PT:Portugal|PW:Palau|PY:Paraguay|QA:Qatar|RE:Réunion|RO:Romania|RS:Serbia|RU:Russia|RW:Rwanda|SA:Saudi Arabia|SB:Solomon Islands|SC:Seychelles|SD:Sudan|SE:Sweden|SG:Singapore|SH:Saint Helena, Ascension and Tristan da Cunha|SI:Slovenia|SJ:Svalbard and Jan Mayen|SK:Slovakia|SL:Sierra Leone|SM:San Marino|SN:Senegal|SO:Somalia|SR:Suriname|SS:South Sudan|ST:São Tomé and Príncipe|SV:El Salvador|SX:Sint Maarten (Dutch part)|SY:Syria|SZ:Eswatini|TC:Turks and Caicos Islands|TD:Chad|TF:French Southern Territories|TG:Togo|TH:Thailand|TJ:Tajikistan|TK:Tokelau|TL:Timor-Leste|TM:Turkmenistan|TN:Tunisia|TO:Tonga|TR:Türkiye|TT:Trinidad and Tobago|TV:Tuvalu|TW:Taiwan|TZ:Tanzania|UA:Ukraine|UG:Uganda|UM:U.S. Minor Outlying Islands|US:United States|UY:Uruguay|UZ:Uzbekistan|VA:Vatican City|VC:Saint Vincent and the Grenadines|VE:Venezuela|VG:British Virgin Islands|VI:U.S. Virgin Islands|VN:Vietnam|VU:Vanuatu|WF:Wallis and Futuna|WS:Samoa|YE:Yemen|YT:Mayotte|ZA:South Africa|ZM:Zambia|ZW:Zimbabwe';
  const COUNTRIES = Object.freeze(COUNTRY_TABLE.split('|').map(entry => {
    const at = entry.indexOf(':');
    return Object.freeze({ code: entry.slice(0, at), name: entry.slice(at + 1) });
  }));
  const COUNTRY_BY_CODE = new Map(COUNTRIES.map(country => [country.code, country]));
  // Codes people type that ISO doesn't use for that country.
  const CODE_MISTAKES = Object.freeze({ UK: 'GB', EL: 'GR' });
  // Other names people search by (matched without accents or capitals).
  const COUNTRY_ALIASES = Object.freeze({
    GB: ['uk', 'great britain', 'britain', 'england', 'scotland', 'wales', 'northern ireland'],
    US: ['usa', 'us of a', 'america', 'united states of america'],
    NL: ['holland', 'the netherlands'],
    CZ: ['czech republic'],
    TR: ['turkey'],
    SZ: ['swaziland'],
    MK: ['macedonia'],
    MM: ['burma'],
    CI: ['ivory coast', 'cote divoire', 'cote d ivoire'],
    BS: ['the bahamas'],
    GM: ['the gambia'],
    KR: ['korea', 'republic of korea'],
    KP: ['dprk'],
    CD: ['drc', 'congo kinshasa'],
    CG: ['congo brazzaville'],
    AE: ['uae', 'emirates'],
    VA: ['holy see', 'vatican'],
    TL: ['east timor'],
    CV: ['cabo verde'],
    RU: ['russian federation'],
    VN: ['viet nam'],
    DE: ['deutschland'],
    ES: ['espana'],
    BR: ['brasil'],
    BL: ['saint barts', 'st barts'],
    BQ: ['bonaire', 'sint eustatius', 'saba'],
    SX: ['saint maarten'],
    FK: ['falklands', 'malvinas'],
    MO: ['macau'],
    CN: ['mainland china', 'prc'],
    SH: ['ascension', 'tristan da cunha']
  });

  // ---- Currencies: active ISO 4217 codes (no fund, metal or test codes) --------------
  const CURRENCY_TABLE = 'AED:UAE dirham|AFN:Afghan afghani|ALL:Albanian lek|AMD:Armenian dram|AOA:Angolan kwanza|ARS:Argentine peso|AUD:Australian dollar|AWG:Aruban florin|AZN:Azerbaijani manat|BAM:Bosnia-Herzegovina convertible mark|BBD:Barbadian dollar|BDT:Bangladeshi taka|BHD:Bahraini dinar|BIF:Burundian franc|BMD:Bermudian dollar|BND:Brunei dollar|BOB:Bolivian boliviano|BRL:Brazilian real|BSD:Bahamian dollar|BTN:Bhutanese ngultrum|BWP:Botswana pula|BYN:Belarusian ruble|BZD:Belize dollar|CAD:Canadian dollar|CDF:Congolese franc|CHF:Swiss franc|CLP:Chilean peso|CNY:Chinese yuan|COP:Colombian peso|CRC:Costa Rican colón|CUP:Cuban peso|CVE:Cape Verdean escudo|CZK:Czech koruna|DJF:Djiboutian franc|DKK:Danish krone|DOP:Dominican peso|DZD:Algerian dinar|EGP:Egyptian pound|ERN:Eritrean nakfa|ETB:Ethiopian birr|EUR:Euro|FJD:Fijian dollar|FKP:Falkland Islands pound|GBP:British pound|GEL:Georgian lari|GHS:Ghanaian cedi|GIP:Gibraltar pound|GMD:Gambian dalasi|GNF:Guinean franc|GTQ:Guatemalan quetzal|GYD:Guyanese dollar|HKD:Hong Kong dollar|HNL:Honduran lempira|HTG:Haitian gourde|HUF:Hungarian forint|IDR:Indonesian rupiah|ILS:Israeli new shekel|INR:Indian rupee|IQD:Iraqi dinar|IRR:Iranian rial|ISK:Icelandic króna|JMD:Jamaican dollar|JOD:Jordanian dinar|JPY:Japanese yen|KES:Kenyan shilling|KGS:Kyrgyzstani som|KHR:Cambodian riel|KMF:Comorian franc|KPW:North Korean won|KRW:South Korean won|KWD:Kuwaiti dinar|KYD:Cayman Islands dollar|KZT:Kazakhstani tenge|LAK:Lao kip|LBP:Lebanese pound|LKR:Sri Lankan rupee|LRD:Liberian dollar|LSL:Lesotho loti|LYD:Libyan dinar|MAD:Moroccan dirham|MDL:Moldovan leu|MGA:Malagasy ariary|MKD:Macedonian denar|MMK:Myanmar kyat|MNT:Mongolian tögrög|MOP:Macanese pataca|MRU:Mauritanian ouguiya|MUR:Mauritian rupee|MVR:Maldivian rufiyaa|MWK:Malawian kwacha|MXN:Mexican peso|MYR:Malaysian ringgit|MZN:Mozambican metical|NAD:Namibian dollar|NGN:Nigerian naira|NIO:Nicaraguan córdoba|NOK:Norwegian krone|NPR:Nepalese rupee|NZD:New Zealand dollar|OMR:Omani rial|PAB:Panamanian balboa|PEN:Peruvian sol|PGK:Papua New Guinean kina|PHP:Philippine peso|PKR:Pakistani rupee|PLN:Polish złoty|PYG:Paraguayan guaraní|QAR:Qatari riyal|RON:Romanian leu|RSD:Serbian dinar|RUB:Russian ruble|RWF:Rwandan franc|SAR:Saudi riyal|SBD:Solomon Islands dollar|SCR:Seychellois rupee|SDG:Sudanese pound|SEK:Swedish krona|SGD:Singapore dollar|SHP:Saint Helena pound|SLE:Sierra Leonean leone|SOS:Somali shilling|SRD:Surinamese dollar|SSP:South Sudanese pound|STN:São Tomé and Príncipe dobra|SVC:Salvadoran colón|SYP:Syrian pound|SZL:Eswatini lilangeni|THB:Thai baht|TJS:Tajikistani somoni|TMT:Turkmenistan manat|TND:Tunisian dinar|TOP:Tongan paʻanga|TRY:Turkish lira|TTD:Trinidad and Tobago dollar|TWD:New Taiwan dollar|TZS:Tanzanian shilling|UAH:Ukrainian hryvnia|UGX:Ugandan shilling|USD:US dollar|UYU:Uruguayan peso|UZS:Uzbekistani som|VED:Venezuelan digital bolívar|VES:Venezuelan bolívar|VND:Vietnamese đồng|VUV:Vanuatu vatu|WST:Samoan tālā|XAF:Central African CFA franc|XCD:East Caribbean dollar|XCG:Caribbean guilder|XOF:West African CFA franc|XPF:CFP franc|YER:Yemeni rial|ZAR:South African rand|ZMW:Zambian kwacha|ZWG:Zimbabwe Gold';
  const COMMON_CURRENCIES = Object.freeze(['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'NZD', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON', 'JPY', 'INR', 'SGD', 'HKD', 'MXN', 'BRL', 'ZAR', 'ILS', 'AED']);
  const CURRENCIES = Object.freeze(CURRENCY_TABLE.split('|').map(entry => {
    const at = entry.indexOf(':');
    const code = entry.slice(0, at);
    return Object.freeze({ code, name: entry.slice(at + 1), common: COMMON_CURRENCIES.includes(code) });
  }));
  const CURRENCY_BY_CODE = new Map(CURRENCIES.map(currency => [currency.code, currency]));
  const validCurrency = code => CURRENCY_BY_CODE.has(code);
  // Decimal places each currency uses (ISO 4217 minor units); most use 2.
  const ZERO_DECIMALS = new Set(['JPY', 'KRW', 'VND', 'CLP', 'ISK', 'UGX', 'PYG', 'XAF', 'XOF', 'XPF', 'KMF', 'GNF', 'RWF', 'DJF', 'BIF', 'VUV']);
  const THREE_DECIMALS = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND']);
  const currencyDecimals = code => (ZERO_DECIMALS.has(code) ? 0 : THREE_DECIMALS.has(code) ? 3 : 2);

  // ---- The made-up sample and the blank form ---------------------------------------
  // DEFAULTS is the blank form ("Start blank"); SAMPLE fills it in for a
  // made-up store, and is what the page shows first.
  const DEFAULTS = Object.freeze({
    storeName: '',
    storeUrl: '',
    logoUrl: '',
    sameAs: '',
    orgType: 'OnlineStore',
    currency: 'USD',
    returnMode: '',
    returnDays: '',
    returnFees: '',
    returnFeeAmount: '',
    returnMethods: Object.freeze([]),
    refundTypes: Object.freeze([]),
    itemConditions: Object.freeze([]),
    returnCountry: '',
    returnLink: '',
    returnCountries: Object.freeze([]),
    shipping: true,
    shipScope: 'countries',
    shipCountries: Object.freeze([]),
    shipRate: 'unstated',
    shipRateAmount: '',
    handlingMin: '',
    handlingMax: '',
    transitMin: '',
    transitMax: '',
    scriptTags: true,
    layout: 'pretty',
    pagesMatch: false
  });
  const SAMPLE = Object.freeze({
    ...DEFAULTS,
    storeName: 'Juniper & Oak Candle Co.',
    storeUrl: 'https://www.juniperandoak.example/',
    returnMode: 'finite',
    returnDays: '30',
    returnFees: 'ReturnShippingFees',
    returnFeeAmount: '6.95',
    returnMethods: Object.freeze(['ReturnByMail']),
    refundTypes: Object.freeze(['FullRefund']),
    itemConditions: Object.freeze(['NewCondition']),
    returnCountry: 'US',
    returnLink: 'https://www.juniperandoak.example/returns',
    returnCountries: Object.freeze(['US']),
    shipping: true,
    shipCountries: Object.freeze(['US']),
    shipRate: 'flat',
    shipRateAmount: '5.95',
    handlingMin: '1',
    handlingMax: '2',
    transitMin: '3',
    transitMax: '5'
  });
  // The fields that describe one store's policy, as opposed to how the
  // markup is written out (script tags, layout).
  const POLICY_KEYS = Object.freeze(Object.keys(DEFAULTS).filter(key => !['scriptTags', 'layout'].includes(key)));

  // ---- Small text helpers ----------------------------------------------------------
  const code = value => `<code>${esc(value)}</code>`;
  const quote = value => `“${esc(value)}”`;
  const number = value => Number(value).toLocaleString('en-US');
  const plural = (count, one, many = `${one}s`) => `${number(count)} ${count === 1 ? one : many}`;
  const joinWords = (words, last = 'and') => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} ${last} ${words[words.length - 1]}`);
  const text = value => (value == null ? '' : String(value));
  // Keep "1–2 days" or "6.95 USD" on one line.
  const nowrap = html => `<span class="wpm-nowrap">${html}</span>`;
  const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };
  const plainText = html => String(html).replace(/<[^>]+>/g, '').replace(/&(?:amp|lt|gt|quot|#39);/g, entity => ENTITIES[entity]);
  // Invisible marks pasted with text from spreadsheets and web pages.
  const INVISIBLE = /[\u200b-\u200d\u2060\ufeff]/g;
  const shorten = (value, max = 48) => (value.length > max ? `${value.slice(0, max - 1)}…` : value);
  const one = list => (list.length === 1 ? list[0] : list.slice());

  // Accents, capitals and punctuation don't decide a country match.
  function fold(value) {
    return text(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      .replace(/[’'`.]/g, '').replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim()
      .replace(/^st /, 'saint ').replace(/^the /, '');
  }

  // ---- Parsing what was typed ---------------------------------------------------------

  // A full https:// web address. Fixes are offered only when the intent is clear.
  function parseUrl(input) {
    const typed = text(input).replace(INVISIBLE, '').replace(/[\t\r\n]/g, '').trim();
    if (!typed) return { ok: false, error: 'empty', typed };
    if (/\s/.test(typed)) return { ok: false, error: 'spaces', typed };
    // "https//shop.com": the colon is missing, so it reads as a path.
    const noColon = /^(h?t{1,3}p{1,2}s{0,2})\/{1,3}(?=[^/])/i.exec(typed);
    if (noColon) return { ok: false, error: 'scheme-typo', typed, typo: noColon[0], fix: `https://${typed.slice(noColon[0].length)}` };
    const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(typed);
    // "shop.example:8080" has a port, not a scheme.
    if (!scheme || /^\d/.test(typed.slice(scheme[0].length))) {
      const candidate = typed.replace(/^\/+/, '');
      const host = candidate.split(/[/?#]/)[0];
      return /^[^\s.@/:]+(\.[^\s.@/:]+)+(:\d+)?$/.test(host)
        ? { ok: false, error: 'no-scheme', typed, fix: `https://${candidate}` }
        : { ok: false, error: 'invalid', typed };
    }
    const protocol = scheme[1].toLowerCase();
    const rest = typed.slice(scheme[0].length);
    // "htps://" or "httsp://": a mistyped https, not another kind of address.
    if (protocol !== 'http' && protocol !== 'https' && /^h?t{1,3}p{1,2}s{0,2}p?$|^htt?s?p$/.test(protocol) && /^\/+[^/]/.test(rest)) {
      return { ok: false, error: 'scheme-typo', typed, typo: `${scheme[0]}//`, fix: `https://${rest.replace(/^\/+/, '')}` };
    }
    if (protocol !== 'http' && protocol !== 'https') return { ok: false, error: 'scheme', typed, scheme: protocol };
    if (!/^\/\//.test(rest)) {
      const candidate = rest.replace(/^\/+/, '');
      return candidate ? { ok: false, error: 'no-scheme', typed, fix: `https://${candidate}` } : { ok: false, error: 'invalid', typed };
    }
    let parsed;
    try { parsed = new URL(typed); } catch (_) { return { ok: false, error: 'invalid', typed }; }
    if (!parsed.hostname || !/^[^.]+(\.[^.]+)+$/.test(parsed.hostname)) return { ok: false, error: 'invalid', typed };
    if (protocol === 'http') return { ok: false, error: 'http', typed, fix: `https://${rest.slice(2)}` };
    // The parser's own spelling: characters an address can't hold are
    // percent-encoded, so the markup always carries a valid URL.
    return { ok: true, url: parsed.href, host: parsed.hostname, path: parsed.pathname, query: parsed.search, hash: parsed.hash, origin: parsed.origin };
  }

  // A store's home page is its domain's root. Free Wix sites live in a folder
  // of wixsite.com, so there the first folder is still the home page.
  function homeAddress(page) {
    if (!page || !page.ok) return null;
    const folders = page.path.split('/').filter(Boolean);
    const hosted = /(^|\.)(wixsite\.com|wixstudio\.(com|io)|editorx\.io)$/i.test(page.host);
    const keep = hosted ? folders.slice(0, 1) : [];
    const home = `${page.origin}/${keep.length ? `${keep.join('/')}` : ''}`;
    const isHome = folders.length <= keep.length && !page.query && !page.hash;
    return { isHome, home };
  }

  // A whole number of days in a range. "30 days" gets a one-tap fix.
  function parseWholeNumber(input, { min = 0, max = Infinity } = {}) {
    const typed = tidyText(input);
    if (!typed) return { ok: false, error: 'empty', typed };
    const digits = /^\d+$/.test(typed) ? typed : null;
    if (!digits) {
      const unit = /^(\d+)\s*(?:(?:business|working|calendar)\s+)?(?:days?|d)\.?$/i.exec(typed);
      if (unit) return { ok: false, error: 'unit', typed, fix: String(Number(unit[1])) };
      if (/^\d*[.,]\d+$/.test(typed)) return { ok: false, error: 'fraction', typed };
      if (/^-\s*\d/.test(typed)) return { ok: false, error: 'low', typed };
      return { ok: false, error: 'invalid', typed };
    }
    const value = Number(digits);
    if (value < min) return { ok: false, error: 'low', typed, value };
    if (value > max) return { ok: false, error: 'high', typed, value };
    return { ok: true, value, typed };
  }

  // A plain amount: digits and an optional period for the decimals. Google
  // asks for no currency symbols, thousands separators or spaces.
  const CURRENCY_MARK = /^(?:[A-Z]{3}|[A-Z]{1,2}\$|[$€£¥₹₩₽₺₪₫฿₱₦₴₸₲₡₵₭₮₼₾])\s*|\s*(?:[A-Z]{3}|[$€£¥₹₩₽₺₪₫฿₱₦₴₸₲₡₵₭₮₼₾])$/gi;
  function parseAmount(input) {
    const typed = text(input).replace(INVISIBLE, '').trim();
    if (!typed) return { ok: false, error: 'empty', typed };
    let rest = typed.replace(CURRENCY_MARK, '').trim();
    const symbol = rest !== typed;
    if (/^-/.test(rest)) return { ok: false, error: 'negative', typed };
    let error = symbol ? 'symbol' : '';
    const spaced = rest.replace(/[\s\u00a0\u2009\u202f']/g, '');
    if (spaced !== rest) { error = error || 'spaces'; rest = spaced; }
    let clean = null;
    if (/^\d+(\.\d+)?$/.test(rest)) clean = rest;
    else if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(rest)) { clean = rest.replace(/,/g, ''); error = error || 'thousands'; }
    else if (/^\d{1,3}(\.\d{3})+,\d{1,2}$/.test(rest)) { clean = rest.replace(/\./g, '').replace(',', '.'); error = error || 'thousands'; }
    else if (/^\d+,\d{1,2}$/.test(rest)) { clean = rest.replace(',', '.'); error = error || 'comma'; }
    if (clean === null) return { ok: false, error: 'invalid', typed };
    // More than nine digits before the point is a typo, and JSON would
    // write it in exponent form.
    if (clean.split('.')[0].replace(/^0+(?=\d)/, '').length > 9) return { ok: false, error: 'big', typed };
    // "6.950" stays 6.95; "007" is 7.
    const fix = String(Number(clean));
    if (error) return { ok: false, error, typed, fix };
    return { ok: true, value: Number(clean), typed };
  }

  // An amount in a currency: a plain number with no more decimal places than
  // the currency has (none for JPY, three for KWD, two for most).
  function parseMoney(input, currency) {
    const amount = parseAmount(input);
    if (!amount.ok) return amount;
    const places = (amount.typed.split('.')[1] || '').replace(/0+$/, '').length;
    const allowed = currencyDecimals(currency);
    if (places > allowed) return { ok: false, error: 'decimals', typed: amount.typed, places, allowed, currency };
    return amount;
  }

  // Codes as typed, checked against ISO 3166-1: valid ones once each, in
  // order, plus what was wrong with the others.
  function countryList(input) {
    const raw = Array.isArray(input) ? input : text(input).split(/[\s,;|]+/);
    const codes = [];
    const invalid = [];
    const duplicates = [];
    raw.map(item => tidyText(item).toUpperCase()).filter(Boolean).forEach(item => {
      if (!COUNTRY_BY_CODE.has(item)) { if (!invalid.includes(item)) invalid.push(item); return; }
      if (codes.includes(item)) { if (!duplicates.includes(item)) duplicates.push(item); return; }
      codes.push(item);
    });
    return { codes, invalid, duplicates };
  }

  const countryName = code => (COUNTRY_BY_CODE.get(code) || { name: code }).name;

  const SEARCH_INDEX = COUNTRIES.map(country => {
    const folded = fold(country.name);
    return { ...country, folded, words: folded.split(' '), aliases: (COUNTRY_ALIASES[country.code] || []).map(fold) };
  });

  // Countries whose name, code or common other name matches what was typed,
  // best first. `taken` marks ones already on the list.
  function searchCountries(query, { taken = [], limit = 8 } = {}) {
    const typed = tidyText(query);
    const folded = fold(typed);
    if (!folded) return [];
    const upper = typed.toUpperCase();
    const fixed = CODE_MISTAKES[upper];
    const found = [];
    SEARCH_INDEX.forEach(country => {
      let score = 0;
      if (upper.length === 2 && country.code === upper) score = 100;
      else if (fixed && country.code === fixed) score = 95;
      else if (country.folded === folded) score = 90;
      else if (country.aliases.includes(folded)) score = 85;
      else if (country.folded.startsWith(folded)) score = 70;
      else if (folded.length >= 2 && country.aliases.some(alias => alias.startsWith(folded))) score = 60;
      else if (country.words.some(word => word.startsWith(folded))) score = 50;
      else if (folded.length >= 3 && country.folded.includes(folded)) score = 30;
      if (score) found.push({ code: country.code, name: country.name, score, taken: taken.includes(country.code) });
    });
    found.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name, 'en'));
    return found.slice(0, limit);
  }

  // One typed country, by code, a common mistake ("UK") or its exact name.
  function resolveCountry(token) {
    const typed = tidyText(token);
    const upper = typed.toUpperCase();
    if (/^[A-Z]{2}$/.test(upper)) {
      if (COUNTRY_BY_CODE.has(upper)) return { code: upper };
      if (CODE_MISTAKES[upper]) return { code: CODE_MISTAKES[upper], from: upper };
      return null;
    }
    const folded = fold(typed);
    const match = SEARCH_INDEX.find(country => country.folded === folded || country.aliases.includes(folded));
    return match ? { code: match.code } : null;
  }

  // A pasted list ("US, CA, GB" or "US CA GB", or names separated by
  // commas). Returns null when the text is one search, not a list.
  function parseCountryPaste(input) {
    const typed = text(input).replace(INVISIBLE, '');
    let tokens = typed.split(/[,;\n\r|]+/).map(tidyText).filter(Boolean);
    if (tokens.length === 1) {
      const words = tokens[0].split(/\s+/);
      if (words.length > 1 && words.every(word => /^[A-Za-z]{2}$/.test(word))) tokens = words;
    }
    if (tokens.length < 2) return null;
    const codes = [];
    const corrected = [];
    const unknown = [];
    tokens.forEach(token => {
      const found = resolveCountry(token);
      if (!found) { if (!unknown.includes(token)) unknown.push(token); return; }
      if (found.from && !corrected.some(item => item.from === found.from)) corrected.push(found);
      if (!codes.includes(found.code)) codes.push(found.code);
    });
    return { codes, corrected, unknown };
  }

  // Profiles elsewhere (sameAs): one address per line.
  function profileList(input) {
    return text(input).split(/\r\n|\n|\r/).map((line, index) => ({ line: index + 1, ...parseUrl(line) })).filter(item => item.error !== 'empty');
  }

  function dayRange(minInput, maxInput) {
    const minTyped = tidyText(minInput);
    const maxTyped = tidyText(maxInput);
    if (!minTyped && !maxTyped) return { ok: false, error: 'empty' };
    if (!minTyped || !maxTyped) return { ok: false, error: 'half', missing: minTyped ? 'max' : 'min', other: minTyped || maxTyped };
    const min = parseWholeNumber(minTyped, { min: 0, max: MAX_SHIP_DAYS });
    const max = parseWholeNumber(maxTyped, { min: 0, max: MAX_SHIP_DAYS });
    if (!min.ok || !max.ok) return { ok: false, error: (!min.ok ? min : max).error === 'high' ? 'high' : 'number', min, max };
    if (min.value > max.value) return { ok: false, error: 'order', min: min.value, max: max.value };
    return { ok: true, min: min.value, max: max.value };
  }

  // ---- State ---------------------------------------------------------------------------

  // Any partial or messy state becomes a complete one, with Google's own
  // names in a fixed order. Country codes keep their typing (and mistakes),
  // so the checks can point at them.
  function normalizeState(input = {}) {
    const source = input || {};
    const has = key => source[key] !== undefined && source[key] !== null;
    const str = key => (has(key) ? text(source[key]) : DEFAULTS[key]);
    const pick = (key, allowed) => (allowed.includes(source[key]) ? source[key] : DEFAULTS[key]);
    const many = (key, allowed) => {
      const value = has(key) ? source[key] : DEFAULTS[key];
      const items = Array.isArray(value) ? value.map(text) : text(value).split(/[\s,]+/);
      return allowed.filter(item => items.includes(item));
    };
    const codes = key => {
      const value = has(key) ? source[key] : DEFAULTS[key];
      const items = Array.isArray(value) ? value : text(value).split(/[\s,;|]+/);
      return items.map(item => tidyText(item).toUpperCase()).filter(Boolean);
    };
    const flag = key => (has(key) ? source[key] !== false && source[key] !== 'false' : DEFAULTS[key]);
    return {
      storeName: str('storeName'),
      storeUrl: str('storeUrl'),
      logoUrl: str('logoUrl'),
      sameAs: str('sameAs'),
      orgType: pick('orgType', ORG_TYPES),
      currency: has('currency') ? tidyText(source.currency).toUpperCase() : DEFAULTS.currency,
      returnMode: pick('returnMode', ['', ...RETURN_MODES]),
      returnDays: str('returnDays'),
      returnFees: pick('returnFees', ['', ...RETURN_FEES]),
      returnFeeAmount: str('returnFeeAmount'),
      returnMethods: many('returnMethods', RETURN_METHODS),
      refundTypes: many('refundTypes', REFUND_TYPES),
      itemConditions: many('itemConditions', ITEM_CONDITIONS),
      returnCountry: has('returnCountry') ? tidyText(source.returnCountry).toUpperCase() : DEFAULTS.returnCountry,
      returnLink: str('returnLink'),
      returnCountries: codes('returnCountries'),
      shipping: flag('shipping'),
      shipScope: pick('shipScope', SHIP_SCOPES),
      shipCountries: codes('shipCountries'),
      shipRate: pick('shipRate', SHIP_RATES),
      shipRateAmount: str('shipRateAmount'),
      handlingMin: str('handlingMin'),
      handlingMax: str('handlingMax'),
      transitMin: str('transitMin'),
      transitMax: str('transitMax'),
      scriptTags: flag('scriptTags'),
      layout: pick('layout', LAYOUTS),
      pagesMatch: source.pagesMatch === true
    };
  }

  const accepts = state => state.returnMode === 'finite' || state.returnMode === 'unlimited';
  const money = (value, currency) => ({ '@type': 'MonetaryAmount', value, currency });
  // Google's shipping policy page types handlingTime and transitTime as a
  // ServicePeriod whose duration holds the range.
  const days = range => ({ '@type': 'ServicePeriod', duration: { '@type': 'QuantitativeValue', minValue: range.min, maxValue: range.max, unitCode: 'DAY' } });

  // ---- The markup ----------------------------------------------------------------------

  // The JSON-LD object: only values that were filled in and are valid, in a
  // fixed order. Invalid values are left out (the checks say why), so the
  // result is always valid JSON.
  function buildObject(input) {
    const s = normalizeState(input);
    const object = { '@context': 'https://schema.org', '@type': s.orgType };
    const name = tidyText(s.storeName);
    if (name) object.name = name;
    const home = parseUrl(s.storeUrl);
    if (home.ok) object.url = home.url;
    const logo = parseUrl(s.logoUrl);
    if (logo.ok) object.logo = logo.url;
    const profiles = profileList(s.sameAs).filter(item => item.ok).map(item => item.url);
    if (profiles.length) object.sameAs = one(profiles);

    const policy = { '@type': 'MerchantReturnPolicy' };
    const covered = countryList(s.returnCountries).codes;
    if (covered.length) policy.applicableCountry = one(covered);
    // The country items are sent back to, when returns are taken.
    if (accepts(s) && COUNTRY_BY_CODE.has(s.returnCountry)) policy.returnPolicyCountry = s.returnCountry;
    if (s.returnMode) policy.returnPolicyCategory = SCHEMA + RETURN_CATEGORIES[s.returnMode];
    if (s.returnMode === 'finite') {
      const returnWindow = parseWholeNumber(s.returnDays, { min: 1, max: MAX_RETURN_DAYS });
      if (returnWindow.ok) policy.merchantReturnDays = returnWindow.value;
    }
    if (accepts(s)) {
      if (s.returnMethods.length) policy.returnMethod = one(s.returnMethods.map(value => SCHEMA + value));
      if (s.returnFees) {
        policy.returnFees = SCHEMA + s.returnFees;
        // Google wants the fee with ReturnShippingFees, and above zero.
        if (s.returnFees === 'ReturnShippingFees') {
          const fee = parseMoney(s.returnFeeAmount, s.currency);
          if (fee.ok && fee.value > 0 && validCurrency(s.currency)) policy.returnShippingFeesAmount = money(fee.value, s.currency);
        }
      }
      if (s.refundTypes.length) policy.refundType = one(s.refundTypes.map(value => SCHEMA + value));
      if (s.itemConditions.length) policy.itemCondition = one(s.itemConditions.map(value => SCHEMA + value));
    }
    const link = parseUrl(s.returnLink);
    if (link.ok) policy.merchantReturnLink = link.url;
    object.hasMerchantReturnPolicy = policy;

    if (s.shipping) {
      const conditions = { '@type': 'ShippingConditions' };
      // Worldwide leaves the destination out, which Google reads as everywhere.
      const places = s.shipScope === 'countries' ? countryList(s.shipCountries).codes : [];
      if (places.length) conditions.shippingDestination = one(places.map(place => ({ '@type': 'DefinedRegion', addressCountry: place })));
      if (validCurrency(s.currency)) {
        if (s.shipRate === 'free') conditions.shippingRate = money(0, s.currency);
        if (s.shipRate === 'flat') {
          const rate = parseMoney(s.shipRateAmount, s.currency);
          if (rate.ok) conditions.shippingRate = money(rate.value, s.currency);
        }
      }
      const transit = dayRange(s.transitMin, s.transitMax);
      if (transit.ok) conditions.transitTime = days(transit);
      const service = { '@type': 'ShippingService', shippingConditions: conditions };
      const handling = dayRange(s.handlingMin, s.handlingMax);
      if (handling.ok) service.handlingTime = days(handling);
      object.hasShippingService = service;
    }
    return object;
  }

  // "<" and ">" travel as \u003c and \u003e, so no value can close the
  // script tag or open an HTML comment wherever the block is pasted. Line
  // and paragraph separators are escaped too, so each field stays on one
  // line, and so are the marks that reorder text right to left
  // (U+202A-U+202E, U+2066-U+2069), so the box shows the block as it is.
  function escapeJson(json) {
    return json.replace(/</g, '\\u003c').replace(/>/g, '\\u003e')
      .replace(/[\u2028\u2029\u202a-\u202e\u2066-\u2069]/g, mark => `\\u${mark.charCodeAt(0).toString(16).padStart(4, '0')}`);
  }

  // The block as it will be pasted, and its length as Wix would count it:
  // every character in the box, spaces and tags included.
  function buildMarkup(input, options = {}) {
    const s = normalizeState(input);
    const scriptTags = options.scriptTags == null ? s.scriptTags : options.scriptTags !== false;
    const layout = LAYOUTS.includes(options.layout) ? options.layout : options.pretty === true ? 'pretty' : options.pretty === false ? 'line' : s.layout;
    const object = buildObject(s);
    const json = escapeJson(layout === 'pretty' ? JSON.stringify(object, null, 2) : JSON.stringify(object));
    const markup = !scriptTags ? json : layout === 'pretty' ? `${OPEN_TAG}\n${json}\n${CLOSE_TAG}` : `${OPEN_TAG}${json}${CLOSE_TAG}`;
    return { object, json, text: markup, characters: markup.length, lines: markup.split('\n').length, scriptTags, layout };
  }

  // ---- Checks ----------------------------------------------------------------------------

  const LEVEL_ORDER = { fix: 0, check: 1, fine: 2, info: 3 };
  const LEVEL_WORDS = Object.freeze({ fix: 'Fix', check: 'Check', fine: 'Fine', info: 'Note' });

  const URL_WORDS = {
    storeUrl: 'home page address',
    logoUrl: 'logo address',
    returnLink: 'returns page address'
  };

  // What's wrong with a typed address, as { title, detail, action }.
  function urlProblem(page, field) {
    const what = URL_WORDS[field];
    const use = page.fix ? { label: `Use ${shorten(page.fix)}`, patch: { [field]: page.fix } } : undefined;
    if (page.error === 'no-scheme') return { title: `Add https:// to the ${what}`, detail: 'Markup needs the full address, starting with https://.', action: use };
    if (page.error === 'http') return { title: `Use https:// in the ${what}`, detail: 'It starts with http://. Use the secure https:// address of your site.', action: use };
    if (page.error === 'scheme-typo') return { title: `Check the start of the ${what}`, detail: `It starts with ${code(page.typo)}. Web addresses start with https://.`, action: use };
    if (page.error === 'scheme') return { title: `Use a web address for the ${what}`, detail: `It starts with ${code(`${page.scheme}:`)}, not https://.` };
    if (page.error === 'spaces') return { title: `Take the spaces out of the ${what}`, detail: 'Web addresses have no spaces. Copy it from your browser’s address bar.' };
    return { title: `Check the ${what}`, detail: 'This doesn’t look like a web address. Copy it from your browser’s address bar, starting with https://.' };
  }

  const AMOUNT_TITLES = {
    symbol: 'Leave out the currency symbol',
    spaces: 'Leave out the spaces',
    thousands: 'Leave out the thousands separator',
    comma: 'Use a period before the cents',
    negative: 'Use an amount of 0 or more',
    invalid: 'Use a plain number'
  };

  // Shared by the return fee and the flat shipping price.
  function amountProblem(amount, field) {
    if (amount.error === 'decimals') {
      const currency = esc(amount.currency);
      return amount.allowed === 0
        ? { title: `${currency} amounts are whole numbers`, detail: 'Type a whole number, like 600, or check the Currency setting.' }
        : { title: `Use ${amount.allowed} digits or fewer after the point`, detail: `${currency} amounts have ${amount.allowed} digits after the point, like ${code(amount.allowed === 3 ? '1.250' : '6.95')}. Check the amount or the Currency setting.` };
    }
    if (amount.error === 'big') return { title: 'Check the amount', detail: 'It has more than nine digits before the point.' };
    const action = amount.fix ? { label: `Use ${amount.fix}`, patch: { [field]: amount.fix } } : undefined;
    const detail = amount.error === 'comma'
      ? `Google reads a period as the decimal point, so use ${code(amount.fix)}.`
      : amount.error === 'invalid' || amount.error === 'negative'
        ? 'Type just the number, like 6.95. The currency comes from the Currency setting.'
        : `Google wants just the number, like ${code(amount.fix)}, with no currency symbol, thousands separator or spaces. The currency comes from the Currency setting.`;
    return { title: AMOUNT_TITLES[amount.error] || AMOUNT_TITLES.invalid, detail, action };
  }

  // Wrong, repeated or missing country codes in one of the two lists.
  function countryProblems(add, { id, field, raw, required, max }) {
    const list = countryList(raw);
    if (list.invalid.length) {
      const fixes = list.invalid.map(item => CODE_MISTAKES[item]).filter(Boolean);
      const allFixable = fixes.length === list.invalid.length;
      const names = list.invalid.map(quote);
      const hints = list.invalid.filter(item => CODE_MISTAKES[item]).map(item => `${esc(countryName(CODE_MISTAKES[item]))} is ${esc(CODE_MISTAKES[item])}.`);
      const replaced = countryList(raw.map(item => {
        const upper = tidyText(item).toUpperCase();
        return CODE_MISTAKES[upper] || upper;
      })).codes;
      add(`${id}-invalid`, 'fix', `${joinWords(names)} ${list.invalid.length === 1 ? 'isn’t a country code' : 'aren’t country codes'}`, `Google needs two-letter ISO 3166-1 codes, like US or GB.${hints.length ? ` ${hints.join(' ')}` : ''}`, {
        field,
        action: allFixable
          ? { label: `Use ${joinWords(fixes.filter((item, index) => fixes.indexOf(item) === index))}`, patch: { [field]: replaced } }
          : { label: list.invalid.length === 1 ? 'Remove it' : 'Remove them', patch: { [field]: list.codes } }
      });
    }
    if (list.duplicates.length) {
      add(`${id}-twice`, 'fix', `${joinWords(list.duplicates)} ${list.duplicates.length === 1 ? 'is' : 'are'} listed twice`, 'Each country goes in once.', { field, action: { label: 'Remove the repeats', patch: { [field]: list.codes } } });
    }
    if (max && list.codes.length > max) {
      add(`${id}-many`, 'fix', `Use ${max} countries or fewer`, `Google takes up to ${max} countries in ${code('applicableCountry')}. This list has ${list.codes.length}.`, { field });
    }
    if (required && !list.codes.length && !list.invalid.length) add(`${id}-none`, 'fix', required.title, required.detail, { field, action: required.action });
    return list;
  }

  // Every check on a state, worst first, and the markup they describe.
  // status: 'fix' (something blocks copying), 'check' (a reminder is open)
  // or 'ready'.
  function validate(input) {
    const s = normalizeState(input);
    const result = buildMarkup(s);
    const checks = [];
    const add = (id, level, title, detail, extra = {}) => checks.push({ id, level, title, detail, ...extra });

    // Your store.
    if (!tidyText(s.storeName)) add('store-name', 'fix', 'Add your store name', 'Use the name shoppers see on your site. Google says it should match your site’s name.', { field: 'storeName' });
    const home = parseUrl(s.storeUrl);
    if (home.error === 'empty') add('store-url', 'fix', 'Add your home page address', 'The address of your store’s home page, starting with https://.', { field: 'storeUrl' });
    else if (!home.ok) {
      const problem = urlProblem(home, 'storeUrl');
      add('store-url', 'fix', problem.title, problem.detail, { field: 'storeUrl', action: problem.action });
    } else {
      const root = homeAddress(home);
      if (!root.isHome) add('store-url-home', 'check', 'Use your home page address', `This is the address of one page (${code(shorten(home.path + home.query + home.hash, 40))}). The markup describes your whole store.`, { field: 'storeUrl', action: { label: `Use ${shorten(root.home)}`, patch: { storeUrl: root.home } } });
    }
    const logo = parseUrl(s.logoUrl);
    if (!logo.ok && logo.error !== 'empty') {
      const problem = urlProblem(logo, 'logoUrl');
      add('logo-url', 'fix', problem.title, problem.detail, { field: 'logoUrl', action: problem.action });
    }
    const profiles = profileList(s.sameAs);
    if (profiles.length > MAX_PROFILES) add('profiles-many', 'fix', `Use ${MAX_PROFILES} profile addresses or fewer`, `This list has ${profiles.length}. This tool takes up to ${MAX_PROFILES}, so keep your main ones.`, { field: 'sameAs' });
    const badProfiles = profiles.filter(item => !item.ok);
    if (badProfiles.length) {
      const fixable = badProfiles.every(item => item.fix);
      const lines = joinWords(badProfiles.map(item => String(item.line)));
      const patched = text(s.sameAs).split(/\r\n|\n|\r/).map(line => {
        const page = parseUrl(line);
        return !page.ok && page.fix ? page.fix : line;
      }).join('\n');
      add('profiles', 'fix', badProfiles.length === 1 ? `Check the profile address on line ${lines}` : `Check the profile addresses on lines ${lines}`, 'Each line needs one full web address, starting with https://.', {
        field: 'sameAs',
        action: fixable ? { label: badProfiles.length === 1 ? `Use ${shorten(badProfiles[0].fix)}` : 'Add https:// to them', patch: { sameAs: patched } } : undefined
      });
    }
    if (s.currency && !validCurrency(s.currency) && needsCurrency(s)) add('currency', 'fix', 'Choose a currency', `Google needs a three-letter ISO 4217 code, like USD. ${quote(s.currency)} isn’t one.`, { field: 'currency' });
    else if (!s.currency && needsCurrency(s)) add('currency', 'fix', 'Choose a currency', 'Your prices need a currency, like USD.', { field: 'currency' });

    // Returns.
    if (!s.returnMode) add('return-mode', 'fix', 'Choose how returns work', `Pick a set number of days, no time limit, or no returns. It goes in the markup as ${code('returnPolicyCategory')}.`, { field: 'returnMode' });
    if (s.returnMode === 'finite') {
      const returnWindow = parseWholeNumber(s.returnDays, { min: 1, max: MAX_RETURN_DAYS });
      const required = `Google requires ${code('merchantReturnDays')} when returns have a time limit.`;
      if (returnWindow.error === 'empty') add('return-days', 'fix', 'Add the number of days', `How many days shoppers have to send something back. ${required}`, { field: 'returnDays' });
      else if (returnWindow.error === 'unit') add('return-days', 'fix', 'Type just the number of days', `Use ${code(returnWindow.fix)}. The markup only takes the number.`, { field: 'returnDays', action: { label: `Use ${returnWindow.fix}`, patch: { returnDays: returnWindow.fix } } });
      else if (returnWindow.error === 'fraction') add('return-days', 'fix', 'Use a whole number of days', 'Type a whole number, like 30.', { field: 'returnDays' });
      else if (returnWindow.error === 'low') add('return-days', 'fix', 'Use at least 1 day', 'If you don’t take returns, choose “We don’t accept returns” instead.', { field: 'returnDays', action: { label: 'Choose “We don’t accept returns”', patch: { returnMode: 'none' } } });
      else if (returnWindow.error === 'high') add('return-days', 'fix', 'Check the number of days', 'That’s more than 100 years. If returns have no time limit, choose “Returns accepted with no time limit” instead.', { field: 'returnDays' });
      else if (returnWindow.error) add('return-days', 'fix', 'Use a number of days', 'Type a whole number, like 30.', { field: 'returnDays' });
    }
    if (accepts(s) && s.returnFees === 'ReturnShippingFees') {
      const fee = parseMoney(s.returnFeeAmount, s.currency);
      if (fee.error === 'empty') add('return-fee', 'fix', 'Add your return shipping fee', `The amount you charge, like 6.95. It goes in the markup as ${code('returnShippingFeesAmount')}.`, { field: 'returnFeeAmount' });
      else if (!fee.ok) {
        const problem = amountProblem(fee, 'returnFeeAmount');
        add('return-fee', 'fix', problem.title, problem.detail, { field: 'returnFeeAmount', action: problem.action });
      } else if (fee.value === 0) add('return-fee-zero', 'fix', 'A fee of 0 is a free return', 'With “We charge a set fee”, Google wants a fee above 0. If returns cost the customer nothing, choose “Free for the customer”.', { field: 'returnFeeAmount', action: { label: 'Choose “Free for the customer”', patch: { returnFees: 'FreeReturn' } } });
    }
    if (accepts(s) && s.returnCountry && !COUNTRY_BY_CODE.has(s.returnCountry)) {
      const fixed = CODE_MISTAKES[s.returnCountry];
      add('return-country', 'fix', `${quote(s.returnCountry)} isn’t an ISO country code`, `Google needs a two-letter ISO 3166-1 code, like US or GB.${fixed ? ` ${esc(countryName(fixed))} is ${fixed}.` : ''}`, {
        field: 'returnCountry',
        action: fixed ? { label: `Use ${fixed}`, patch: { returnCountry: fixed } } : { label: 'Clear it', patch: { returnCountry: '' } }
      });
    }
    countryProblems(add, {
      id: 'return-countries',
      field: 'returnCountries',
      raw: s.returnCountries,
      max: MAX_RETURN_COUNTRIES,
      required: { title: 'Add at least one country', detail: `This markup says how returns work, so Google also needs ${code('applicableCountry')}: the countries it covers.` }
    });
    const link = parseUrl(s.returnLink);
    if (!link.ok && link.error !== 'empty') {
      const problem = urlProblem(link, 'returnLink');
      add('return-link', 'fix', problem.title, problem.detail, { field: 'returnLink', action: problem.action });
    }

    // Shipping.
    if (s.shipping) {
      const returnCodes = countryList(s.returnCountries).codes;
      if (s.shipScope === 'countries') {
        countryProblems(add, {
          id: 'ship-countries',
          field: 'shipCountries',
          raw: s.shipCountries,
          required: {
            title: 'Add the countries you ship to',
            detail: 'Or choose Worldwide if you ship everywhere for the same price, or untick “Add your shipping policy” to leave shipping out.',
            action: returnCodes.length ? { label: 'Use the same countries as returns', patch: { shipCountries: returnCodes } } : undefined
          }
        });
      }
      if (s.shipRate === 'flat') {
        const rate = parseMoney(s.shipRateAmount, s.currency);
        if (rate.error === 'empty') add('ship-rate', 'fix', 'Add your shipping price', 'The one price every order pays, like 5.95.', { field: 'shipRateAmount' });
        else if (!rate.ok) {
          const problem = amountProblem(rate, 'shipRateAmount');
          add('ship-rate', 'fix', problem.title, problem.detail, { field: 'shipRateAmount', action: problem.action });
        } else if (rate.value === 0) add('ship-rate-zero', 'check', 'A price of 0 is free shipping', 'If shipping costs nothing, say so.', { field: 'shipRateAmount', action: { label: 'Choose “Free shipping”', patch: { shipRate: 'free' } } });
      }
      rangeProblems(add, 'handling', 'handling time', s.handlingMin, s.handlingMax);
      rangeProblems(add, 'transit', 'delivery time', s.transitMin, s.transitMax);
    }

    // The block as a whole.
    if (result.characters >= LIMIT) {
      let action;
      if (s.layout === 'pretty' && buildMarkup(s, { layout: 'line' }).characters < LIMIT) action = { label: 'Put it on one line', patch: { layout: 'line' } };
      else if (s.scriptTags && buildMarkup(s, { layout: 'line', scriptTags: false }).characters < LIMIT) action = { label: 'Use one line without script tags', patch: { layout: 'line', scriptTags: false } };
      const advice = action ? 'A shorter layout fits.' : 'Shipping countries take the most room. List your main ones, or choose Worldwide if you ship everywhere for the same price.';
      add('length', 'fix', `Too long for Wix: ${number(result.characters)} characters`, `Wix only adds markup that is under ${number(LIMIT)} characters. ${advice}`, { action });
    } else {
      add('length', 'fine', `Under Wix’s limit: ${nowrap(`${number(result.characters)} of ${number(LIMIT)}`)} characters`, `Wix also allows up to ${PER_PAGE} markups and <a href="${WIX_TROUBLESHOOTING}">${number(PAGE_TOTAL)} characters in total</a> on one page. Other markup on the page counts toward that. Wix doesn’t say whether its own preset blocks do.`);
    }

    const policy = result.object.hasMerchantReturnPolicy;
    const complete = policy.applicableCountry && policy.returnPolicyCategory && (s.returnMode !== 'finite' || policy.merchantReturnDays);
    if (complete && !checks.some(item => item.level === 'fix' && /^return-countries/.test(item.id))) {
      const names = ['applicableCountry', 'returnPolicyCategory'].concat(s.returnMode === 'finite' ? ['merchantReturnDays'] : []).map(name => code(name));
      add('required', 'fine', 'Has what Google requires', `Return policy: ${joinWords(names)}.${s.shipping ? ` Shipping policy: ${code('shippingConditions')}.` : ''}`);
    }
    if (accepts(s)) {
      const missing = [!s.returnFees && 'who pays for return shipping', !s.returnMethods.length && 'how items come back', !s.returnCountry && 'where returns are sent'].filter(Boolean);
      if (missing.length) add('recommended', 'info', 'Google recommends more detail', `If your policy says ${joinWords(missing)}, add ${missing.length === 1 ? 'it' : 'them'} in Returns.`);
    }
    add('pages-match', s.pagesMatch ? 'fine' : 'check', 'My returns and shipping pages say the same thing', 'Google’s rules say markup must describe what people can see. Compare “What it tells Google” with your store’s pages.');
    add('precedence', 'info', 'Merchant Center and Search Console settings come first', 'Google uses return and shipping settings from Merchant Center or Search Console over this markup, and markup on a product page beats this store-wide block too. Google’s merchant listing page also puts Merchant Center product feeds first.');

    checks.sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]);
    const status = checks.some(item => item.level === 'fix') ? 'fix' : checks.some(item => item.level === 'check') ? 'check' : 'ready';
    return { status, checks, result, state: s };
  }

  function needsCurrency(s) {
    return (accepts(s) && s.returnFees === 'ReturnShippingFees' && tidyText(s.returnFeeAmount) !== '') || (s.shipping && (s.shipRate === 'free' || s.shipRate === 'flat'));
  }

  function rangeProblems(add, id, what, minInput, maxInput) {
    const range = dayRange(minInput, maxInput);
    const field = id;
    if (range.ok || range.error === 'empty') return;
    if (range.error === 'half') {
      const value = tidyText(range.other);
      const ok = parseWholeNumber(value, { min: 0, max: MAX_SHIP_DAYS }).ok;
      add(id, 'fix', `Add both numbers for ${what}`, 'The fewest and the most days, like 1 to 2. Use the same number twice for an exact time.', {
        field,
        action: ok ? { label: `Use ${value} to ${value} days`, patch: { [`${id}${range.missing === 'max' ? 'Max' : 'Min'}`]: value } } : undefined
      });
    } else if (range.error === 'order') {
      add(id, 'fix', `Put the smaller number first for ${what}`, `It says ${range.min} to ${range.max} days.`, { field, action: { label: `Use ${range.max} to ${range.min} days`, patch: { [`${id}Min`]: String(range.max), [`${id}Max`]: String(range.min) } } });
    } else if (range.error === 'high') {
      add(id, 'fix', `Use ${MAX_SHIP_DAYS} days or fewer for ${what}`, `This tool takes up to ${MAX_SHIP_DAYS} days.`, { field });
    } else {
      add(id, 'fix', `Use whole numbers of days for ${what}`, 'Type numbers like 1 and 2, without words.', { field });
    }
  }

  // ---- What the markup says, in words ------------------------------------------------------

  function countryWords(codes, max = 5) {
    const names = codes.map(countryName);
    if (names.length <= max) return joinWords(names);
    return `${names.slice(0, max - 1).join(', ')} and ${plural(names.length - max + 1, 'more country', 'more countries')}`;
  }
  const amountWords = (amount, currency) => `${amount.toLocaleString('en-US', { maximumFractionDigits: 3 })} ${currency}`;
  const rangeWords = range => (range.minValue === range.maxValue ? plural(range.minValue, 'day') : `${range.minValue}–${range.maxValue} days`);
  const valueList = value => (Array.isArray(value) ? value : value ? [value] : []);
  const enumName = value => String(value).replace(SCHEMA, '');
  const REFUND_WORDS = { FullRefund: 'a full refund', ExchangeRefund: 'an exchange', StoreCreditRefund: 'store credit' };
  const CONDITION_WORDS = { NewCondition: 'new', UsedCondition: 'used', RefurbishedCondition: 'refurbished' };
  const METHOD_WORDS = { ReturnByMail: 'by mail', ReturnInStore: 'in a store', ReturnAtKiosk: 'at a kiosk' };
  const capital = value => value.charAt(0).toUpperCase() + value.slice(1);

  // Rows of [term, words], read from the built object itself, so the words
  // can't say anything the markup doesn't.
  function readBack(input) {
    const s = normalizeState(input);
    const object = buildObject(s);
    const policy = object.hasMerchantReturnPolicy;
    const category = enumName(policy.returnPolicyCategory || '');
    const returns = [];
    returns.push(['Returns',
      category === RETURN_CATEGORIES.finite
        ? (policy.merchantReturnDays ? `Accepted within ${plural(policy.merchantReturnDays, 'day')}` : 'Accepted within a set number of days (add the number)')
        : category === RETURN_CATEGORIES.unlimited ? 'Accepted with no time limit'
          : category === RETURN_CATEGORIES.none ? 'Not accepted' : 'Not chosen yet']);
    const covered = valueList(policy.applicableCountry);
    returns.push(['Covers', covered.length ? countryWords(covered) : 'No country yet']);
    if (accepts(s)) {
      const fees = enumName(policy.returnFees || '');
      const fee = policy.returnShippingFeesAmount;
      returns.push(['Return shipping',
        fees === 'FreeReturn' ? 'Free for the customer'
          : fees === 'ReturnFeesCustomerResponsibility' ? 'The customer pays for and arranges it'
            : fees === 'ReturnShippingFees' ? (fee ? `The customer pays a ${amountWords(fee.value, fee.currency)} fee` : 'The customer pays a set fee (add the amount)')
              : 'Not stated']);
      const methods = valueList(policy.returnMethod).map(value => METHOD_WORDS[enumName(value)]);
      if (methods.length) returns.push(['Sent back', capital(joinWords(methods, 'or'))]);
      if (policy.returnPolicyCountry) returns.push(['Sent back to', countryName(policy.returnPolicyCountry)]);
      const refunds = valueList(policy.refundType).map(value => REFUND_WORDS[enumName(value)]);
      if (refunds.length) returns.push(['Customer gets', capital(joinWords(refunds, 'or'))]);
      const conditions = valueList(policy.itemCondition).map(value => CONDITION_WORDS[enumName(value)]);
      if (conditions.length) returns.push(['Condition', `${capital(joinWords(conditions, 'or'))} items`]);
    }
    if (policy.merchantReturnLink) returns.push(['Returns page', policy.merchantReturnLink.replace(/^https:\/\//i, '')]);

    const shipping = [];
    const service = object.hasShippingService;
    if (!service) shipping.push(['Shipping', 'Not included']);
    else {
      const conditions = service.shippingConditions;
      const places = valueList(conditions.shippingDestination).map(region => region.addressCountry);
      shipping.push(['Ships to', places.length ? countryWords(places) : s.shipScope === 'worldwide' ? 'Worldwide' : 'No country yet']);
      const rate = conditions.shippingRate;
      shipping.push(['Shipping price', !rate ? 'Not stated' : rate.value === 0 ? `Free (0 ${rate.currency})` : amountWords(rate.value, rate.currency)]);
      shipping.push(['Handling time', service.handlingTime ? rangeWords(service.handlingTime.duration) : 'Not stated']);
      shipping.push(['Delivery time', conditions.transitTime ? rangeWords(conditions.transitTime.duration) : 'Not stated']);
    }
    return { returns, shipping };
  }

  // ---- Markup shared by the server render and the browser ------------------------------

  // The block with its parts coloured, one block per line so a long line
  // wraps under its own start instead of the box's left edge. Every piece is
  // escaped; the colours are only spans, so what is copied is result.text.
  const TOKEN = /(<\/?script\b[^>]*>)|("(?:[^"\\\n]|\\.)*")(\s*:)|("(?:[^"\\\n]|\\.)*")|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|(\btrue\b|\bfalse\b|\bnull\b)|([{}[\],:])|(\s+)|([\s\S])/g;
  function tokenMarkup(line) {
    let html = '';
    line.replace(TOKEN, (match, tag, key, colon, string, num, literal, punct, space, other) => {
      if (tag) html += `<span class="wpm-t-tag">${esc(tag)}</span>`;
      else if (key) html += `<span class="wpm-t-key">${esc(key)}</span><span class="wpm-t-punct">${esc(colon)}</span>`;
      // A long address may wrap after a slash rather than mid-word.
      else if (string) html += `<span class="wpm-t-str">${esc(string).replace(/:\/\/|\//g, '$&<wbr>')}</span>`;
      else if (num) html += `<span class="wpm-t-num">${esc(num)}</span>`;
      else if (literal) html += `<span class="wpm-t-num">${esc(literal)}</span>`;
      else if (punct) html += `<span class="wpm-t-punct">${esc(punct)}</span>`;
      else html += esc(space || other || '');
      return match;
    });
    return html;
  }
  function codeMarkup(markupText) {
    return String(markupText).split('\n').map(line => {
      const indent = line.length - line.replace(/^ +/, '').length;
      return `<span class="wpm-line" style="--wpm-indent:${indent}">${tokenMarkup(line) || ' '}</span>`;
    }).join('');
  }

  // Long blocks start folded to a few screens' worth; the page measures the
  // real height once it runs. Lines are a fair guess before that.
  const isLong = result => (result.layout === 'pretty' ? result.lines > 18 : result.characters > 1100);

  function statusLine(validation) {
    const fixes = validation.checks.filter(item => item.level === 'fix');
    const tooLong = fixes.length === 1 && fixes[0].id === 'length';
    const lead = validation.status === 'fix' ? (tooLong ? 'Too long for Wix' : `${plural(fixes.length, 'thing')} to fix`) : 'Ready to paste';
    const characters = validation.result.characters;
    // At or over the limit, "7,000 of 7,000" would read as fine.
    const count = characters >= LIMIT
      ? `${number(characters)} characters (Wix needs fewer than ${number(LIMIT)})`
      : `${number(characters)} of ${number(LIMIT)} characters`;
    return { lead, count, text: `${lead} · ${count}` };
  }

  const meterShare = result => Math.min(100, Math.round(result.characters / LIMIT * 1000) / 10);

  function statusMarkup(validation) {
    const line = statusLine(validation);
    return `<span class="wpm-status-icon" aria-hidden="true"></span><strong>${esc(line.lead)}</strong><span class="wpm-status-sep" aria-hidden="true"> · </span><span class="wpm-status-count">${esc(line.count)}</span>`;
  }

  function readBackMarkup(input) {
    const facts = readBack(input);
    const rows = list => list.map(([term, words]) => `<div><dt>${esc(term)}</dt><dd>${esc(words).replace(/:\/\/|\//g, '$&<wbr>').replace(/(\d+(?:\.\d+)?)(–\d+)? (days?|[A-Z]{3})\b/g, match => nowrap(match))}</dd></div>`).join('');
    return `<div class="wpm-facts-group"><h4>Returns</h4><dl class="wpm-facts">${rows(facts.returns)}</dl></div><div class="wpm-facts-group"><h4>Shipping</h4><dl class="wpm-facts">${rows(facts.shipping)}</dl></div>`;
  }

  // One row of the checks list: an icon and a word, never colour alone.
  function checkRowMarkup(check) {
    const buttons = [
      check.action ? `<button type="button" class="wpm-fix" data-wpm-action="${esc(check.id)}">${esc(check.action.label)}</button>` : '',
      check.field && (check.level === 'fix' || check.level === 'check') ? `<button type="button" class="wpm-goto" data-wpm-goto="${esc(check.field)}">Show me</button>` : ''
    ].filter(Boolean).join('');
    return `<li class="wpm-check" data-level="${check.level}" data-check="${esc(check.id)}"><span class="wpm-badge"><span class="wpm-badge-icon" aria-hidden="true"></span>${LEVEL_WORDS[check.level]}</span><div class="wpm-check-body"><p class="wpm-check-title">${check.title}</p>${check.detail ? `<p class="wpm-check-detail">${check.detail}</p>` : ''}${buttons ? `<div class="wpm-check-actions">${buttons}</div>` : ''}</div></li>`;
  }

  // The checks around the fixed "pages say the same thing" row, which keeps
  // its tick box (and focus) between updates: problems before it, the
  // passing checks and notes after it.
  function checksMarkup(validation) {
    const rows = validation.checks.filter(item => item.id !== 'pages-match');
    return {
      before: rows.filter(item => item.level === 'fix' || item.level === 'check').map(checkRowMarkup).join(''),
      after: rows.filter(item => item.level === 'fine' || item.level === 'info').map(checkRowMarkup).join('')
    };
  }

  function matchRowMarkup(validation) {
    const check = validation.checks.find(item => item.id === 'pages-match');
    return `<li class="wpm-check wpm-check-match" data-level="${check.level}" data-check="pages-match"><span class="wpm-badge"><span class="wpm-badge-icon" aria-hidden="true"></span><span data-wpm-match-word>${LEVEL_WORDS[check.level]}</span></span><div class="wpm-check-body"><label class="wpm-match"><input type="checkbox" data-wpm-field="pagesMatch"${validation.state.pagesMatch ? ' checked' : ''} aria-describedby="wpm-match-detail"><span>${esc(check.title)}</span></label><p class="wpm-check-detail" id="wpm-match-detail">${esc(check.detail)}</p></div></li>`;
  }

  // The problem shown under a field: its first fix or check.
  function fieldProblem(validation, field) {
    return validation.checks.find(item => item.field === field && (item.level === 'fix' || item.level === 'check')) || null;
  }

  function fieldErrorMarkup(check) {
    if (!check) return '';
    return `<p class="wpm-field-error-title">${check.title}</p>${check.detail ? `<p>${check.detail}</p>` : ''}${check.action ? `<button type="button" class="wpm-fix" data-wpm-action="${esc(check.id)}">${esc(check.action.label)}</button>` : ''}`;
  }

  // Is this still the made-up example store?
  function isSample(input) {
    const s = normalizeState(input);
    return tidyText(s.storeName) === SAMPLE.storeName || text(s.storeUrl).trim() === SAMPLE.storeUrl;
  }

  // The line beside Copy: why it's off, or that the store is the example.
  function copyHint(validation) {
    const fixes = validation.checks.filter(item => item.level === 'fix').length;
    if (fixes) return `Fix ${fixes === 1 ? 'the problem' : `the ${fixes} problems`} above to copy.`;
    if (isSample(validation.state)) return 'This is the made-up example store. Put in your own policy before you paste it into Wix.';
    return '';
  }

  // One sentence for screen readers after typing pauses.
  function summaryText(validation) {
    const line = statusLine(validation);
    if (validation.status === 'fix') {
      const first = validation.checks.find(item => item.level === 'fix');
      return `${line.lead}. ${plainText(first.title)}.`;
    }
    return `${line.lead}, ${line.count}.`;
  }

  function chipsMarkup(codes, limit = Infinity) {
    return codes.slice(0, limit).map(item => {
      const known = COUNTRY_BY_CODE.has(item);
      const name = known ? countryName(item) : item;
      return `<li><button type="button" class="wpm-chip${known ? '' : ' wpm-chip-bad'}" data-wpm-remove="${esc(item)}" aria-label="Remove ${esc(name)}"><span class="wpm-chip-name">${esc(name)}</span>${known ? `<span class="wpm-chip-code">${esc(item)}</span>` : ''}<span class="wpm-chip-x" aria-hidden="true"></span></button></li>`;
    }).join('');
  }

  function matchesMarkup(query, taken) {
    const typed = tidyText(query);
    if (!typed) return '';
    const pasted = parseCountryPaste(typed);
    if (pasted) {
      const fresh = pasted.codes.filter(item => !taken.includes(item));
      const label = fresh.length ? `Add ${plural(fresh.length, 'country', 'countries')}: ${shorten(fresh.join(', '), 60)}` : '';
      const unknown = pasted.unknown.length ? `<li class="wpm-no-match">${joinWords(pasted.unknown.map(quote))} ${pasted.unknown.length === 1 ? 'isn’t an ISO country code' : 'aren’t ISO country codes'}.</li>` : '';
      return (label ? `<li><button type="button" class="wpm-option" data-wpm-add="${esc(fresh.join(','))}"><span class="wpm-option-name">${esc(label)}</span></button></li>` : '<li class="wpm-no-match">They’re all on the list already.</li>') + unknown;
    }
    const found = searchCountries(typed, { taken });
    if (!found.length) return `<li class="wpm-no-match">No country matches ${quote(shorten(typed, 30))}. Try its English name or two-letter code.</li>`;
    return found.map(item => item.taken
      ? `<li><span class="wpm-option wpm-option-taken"><span class="wpm-option-name">${esc(item.name)}</span><span class="wpm-chip-code">${esc(item.code)}</span><span class="wpm-option-note">Added</span></span></li>`
      : `<li><button type="button" class="wpm-option" data-wpm-add="${esc(item.code)}"><span class="wpm-option-name">${esc(item.name)}</span><span class="wpm-chip-code">${esc(item.code)}</span></button></li>`).join('');
  }

  // ---- For the /tools directory card ------------------------------------------------------
  // The first lines of the example's markup and the status line the tool
  // prints for it, from the same functions as the page.
  function directoryPreview() {
    const validation = validate(SAMPLE);
    return {
      lines: validation.result.text.split('\n').slice(0, 6),
      characters: validation.result.characters,
      summary: statusLine(validation).text
    };
  }

  // ---- Page behaviour -------------------------------------------------------------------------

  const CHIP_LIMIT = 12;
  const TEXT_FIELDS = ['storeName', 'storeUrl', 'logoUrl', 'sameAs', 'returnDays', 'returnFeeAmount', 'returnLink', 'shipRateAmount', 'handlingMin', 'handlingMax', 'transitMin', 'transitMax'];
  const RADIO_FIELDS = ['orgType', 'returnMode', 'returnFees', 'shipScope', 'shipRate', 'layout'];
  const SELECT_FIELDS = ['currency', 'returnCountry'];
  const BOX_FIELDS = ['returnMethods', 'refundTypes', 'itemConditions'];
  const FLAG_FIELDS = ['shipping', 'scriptTags', 'pagesMatch'];
  const LIST_FIELDS = ['returnCountries', 'shipCountries'];
  // The error box a field's problems show in.
  const errorKey = name => name.replace(/(Min|Max)$/, '');
  // The policy fields compared to tell the example and a blank form apart.
  const FORM_KEYS = POLICY_KEYS.filter(key => key !== 'pagesMatch');
  const SAMPLE_STATE = normalizeState(SAMPLE);
  const BLANK_STATE = normalizeState(DEFAULTS);

  function init(root) {
    if (!root || root.dataset.wpmReady === 'true') return;
    const doc = root.ownerDocument;
    const win = doc.defaultView || (typeof window !== 'undefined' ? window : null);
    const q = selector => root.querySelector(selector);
    const qa = selector => Array.from(root.querySelectorAll(selector));
    const fieldEl = name => q(`[data-wpm-field="${name}"]`);
    const codeEl = q('[data-wpm-code]');
    const copyButton = q('[data-wpm-copy]');
    const checksEl = q('[data-wpm-checks]');
    if (!codeEl || !copyButton || !checksEl || !fieldEl('storeName')) return;
    root.dataset.wpmReady = 'true';

    const codeBox = q('[data-wpm-code-box]');
    const codeMore = q('[data-wpm-code-more]');
    const statusLineEl = q('[data-wpm-status-line]');
    const meterEl = q('[data-wpm-meter]');
    const readBackEl = q('[data-wpm-readback]');
    const downloadButton = q('[data-wpm-download]');
    const copyHintEl = q('[data-wpm-copy-hint]');
    const statusEl = q('[data-wpm-status]');
    const fallback = q('[data-wpm-fallback]');
    const fallbackText = q('[data-wpm-fallback-text]');
    const announcer = q('[data-wpm-announcement]');
    const matchRow = q('[data-check="pages-match"]');
    const lists = {};
    const pickers = {};
    const touched = new Set();
    const errorTimers = new Map();
    let current = null;
    let announceTimer = 0;
    let reportedBuilt = false;
    let codeOpen = false;

    const toolEvent = name => doc.dispatchEvent(new win.CustomEvent('myuser:tool-event', { detail: { name } }));
    const setStatus = message => { if (statusEl) statusEl.textContent = message; };
    // Rewriting unchanged markup would swap a fix button out between its
    // mousedown and click, so the click would never arrive.
    const shown = new Map();
    function setHtml(element, html) {
      if (!element || shown.get(element) === html) return;
      shown.set(element, html);
      element.innerHTML = html;
    }

    function readState() {
      const state = {};
      TEXT_FIELDS.forEach(name => { const el = fieldEl(name); state[name] = el ? el.value : ''; });
      RADIO_FIELDS.forEach(name => { const el = q(`input[type="radio"][name="wpm-${name}"]:checked`); state[name] = el ? el.value : ''; });
      BOX_FIELDS.forEach(name => { state[name] = qa(`input[type="checkbox"][name="wpm-${name}"]:checked`).map(el => el.value); });
      FLAG_FIELDS.forEach(name => { const el = fieldEl(name); state[name] = el ? el.checked : DEFAULTS[name]; });
      LIST_FIELDS.forEach(name => { state[name] = (lists[name] || []).slice(); });
      SELECT_FIELDS.forEach(name => { const el = fieldEl(name); state[name] = el ? el.value : DEFAULTS[name]; });
      return state;
    }

    // Put values into the form: a fix, "Start blank" or "Fill in the example".
    function writeState(patch) {
      Object.keys(patch).forEach(name => {
        const value = patch[name];
        if (TEXT_FIELDS.includes(name) || SELECT_FIELDS.includes(name)) { const el = fieldEl(name); if (el) el.value = text(value); }
        else if (RADIO_FIELDS.includes(name)) qa(`input[type="radio"][name="wpm-${name}"]`).forEach(el => { el.checked = el.value === value; });
        else if (BOX_FIELDS.includes(name)) qa(`input[type="checkbox"][name="wpm-${name}"]`).forEach(el => { el.checked = (value || []).includes(el.value); });
        else if (FLAG_FIELDS.includes(name)) { const el = fieldEl(name); if (el) el.checked = Boolean(value); }
        else if (LIST_FIELDS.includes(name)) { lists[name] = (value || []).map(item => tidyText(item).toUpperCase()).filter(Boolean); if (pickers[name]) pickers[name].draw(); }
      });
    }

    // ---- Country pickers ----
    function setupPicker(name) {
      const picker = q(`[data-wpm-picker="${name}"]`);
      if (!picker) return;
      const chips = picker.querySelector('[data-wpm-chips]');
      const search = picker.querySelector('[data-wpm-search]');
      const matches = picker.querySelector('[data-wpm-matches]');
      const note = picker.querySelector('[data-wpm-picker-note]');
      const more = picker.querySelector('[data-wpm-chips-more]');
      const count = picker.querySelector('[data-wpm-count]');
      const max = name === 'returnCountries' ? MAX_RETURN_COUNTRIES : Infinity;
      lists[name] = text(picker.dataset.wpmCodes).split(',').map(item => item.trim()).filter(Boolean);
      let expanded = false;
      const say = message => { if (note) note.textContent = message; };

      function draw() {
        const codes = lists[name];
        if (codes.length <= CHIP_LIMIT) expanded = false;
        setHtml(chips, chipsMarkup(codes, expanded ? Infinity : CHIP_LIMIT));
        chips.hidden = !codes.length;
        if (more) {
          more.hidden = codes.length <= CHIP_LIMIT;
          more.textContent = expanded ? 'Show fewer' : `Show all ${codes.length}`;
          more.setAttribute('aria-expanded', String(expanded));
        }
        if (count) count.textContent = codes.length ? plural(codes.length, 'country', 'countries') : 'None yet';
        drawMatches();
      }

      function drawMatches() {
        const html = matchesMarkup(search.value, lists[name]);
        setHtml(matches, html);
        matches.hidden = !html;
      }

      function add(codes) {
        const fresh = codes.filter(item => !lists[name].includes(item));
        if (!fresh.length) { say(codes.length === 1 ? `${countryName(codes[0])} is already on the list.` : 'They’re all on the list already.'); return; }
        const added = fresh.slice(0, Math.max(0, max - lists[name].length));
        const left = fresh.length - added.length;
        const parts = [];
        if (added.length === 1) parts.push(`Added ${countryName(added[0])}.`);
        else if (added.length) parts.push(`Added ${plural(added.length, 'country', 'countries')}.`);
        if (left) parts.push(`Google takes up to ${max} countries here, so ${left === 1 ? '1 wasn’t' : `${number(left)} weren’t`} added.`);
        say(parts.join(' '));
        if (!added.length) return;
        lists[name] = lists[name].concat(added);
        touched.add(name);
        draw();
        changed();
      }

      function remove(item, button) {
        const at = lists[name].indexOf(item);
        if (at === -1) return;
        lists[name] = lists[name].filter((entry, index) => index !== at);
        say(`Removed ${countryName(item)}.`);
        touched.add(name);
        draw();
        changed();
        // Keep keyboard users in the list: the next chip, else the search box.
        const buttons = Array.from(chips.querySelectorAll('[data-wpm-remove]'));
        const next = buttons[Math.min(at, buttons.length - 1)];
        if (button && doc.activeElement !== search) (next || search).focus();
      }

      function commit() {
        const typed = search.value;
        if (!tidyText(typed)) return;
        const pasted = parseCountryPaste(typed);
        if (pasted) {
          if (pasted.codes.length) add(pasted.codes);
          const notes = [];
          pasted.corrected.forEach(item => notes.push(`${item.from} went in as ${item.code}, the code for ${countryName(item.code)}.`));
          if (pasted.unknown.length) notes.push(`${joinWords(pasted.unknown.map(item => `“${item}”`))} ${pasted.unknown.length === 1 ? 'isn’t an ISO country code' : 'aren’t ISO country codes'}.`);
          if (notes.length) say(`${note.textContent} ${notes.join(' ')}`.trim());
          search.value = '';
          drawMatches();
          return;
        }
        const found = searchCountries(typed, { taken: lists[name] }).filter(item => !item.taken);
        if (found.length) { add([found[0].code]); search.value = ''; drawMatches(); }
        else say(searchCountries(typed, { taken: lists[name] }).length ? 'That country is already on the list.' : 'No country matches that. Try its English name or two-letter code.');
      }

      chips.addEventListener('click', event => {
        const button = event.target.closest('[data-wpm-remove]');
        if (button) remove(button.dataset.wpmRemove, button);
      });
      search.addEventListener('input', drawMatches);
      search.addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); commit(); }
        else if (event.key === 'Escape' && search.value) { event.preventDefault(); search.value = ''; drawMatches(); }
        else if (event.key === 'ArrowDown') {
          const first = matches.querySelector('button');
          if (first) { event.preventDefault(); first.focus(); }
        }
      });
      matches.addEventListener('click', event => {
        const button = event.target.closest('[data-wpm-add]');
        if (!button) return;
        add(button.dataset.wpmAdd.split(','));
        search.value = '';
        drawMatches();
        // Ready for the next country; a tap on a phone doesn't reopen the keyboard.
        if (event.pointerType !== 'touch') search.focus();
      });
      matches.addEventListener('keydown', event => {
        const buttons = Array.from(matches.querySelectorAll('button'));
        const at = buttons.indexOf(doc.activeElement);
        if (at === -1) return;
        if (event.key === 'ArrowDown' && buttons[at + 1]) { event.preventDefault(); buttons[at + 1].focus(); }
        else if (event.key === 'ArrowUp') { event.preventDefault(); (buttons[at - 1] || search).focus(); }
        else if (event.key === 'Escape') { event.preventDefault(); search.focus(); }
      });
      if (more) more.addEventListener('click', () => { expanded = !expanded; draw(); });
      pickers[name] = { draw, search, say };
    }
    LIST_FIELDS.forEach(setupPicker);
    const same = q('[data-wpm-same]');
    if (same) same.addEventListener('click', () => {
      const codes = countryList(lists.returnCountries).codes;
      writeState({ shipCountries: codes });
      if (pickers.shipCountries) pickers.shipCountries.say(codes.length ? `Ships to the same ${plural(codes.length, 'country', 'countries')} as returns.` : 'Add return countries first.');
      touched.add('shipCountries');
      changed();
    });

    // ---- Problems under fields ----
    function showFieldError(key, html) {
      const box = q(`[data-wpm-error="${key}"]`);
      if (!box) return;
      clearTimeout(errorTimers.get(key));
      setHtml(box, html);
      box.hidden = !html;
      qa(`[data-wpm-error-for="${key}"]`).forEach(el => {
        if (html) el.setAttribute('aria-invalid', 'true');
        else el.removeAttribute('aria-invalid');
      });
    }

    // A new problem waits for a pause in typing; a fix, a paste or a
    // problem already on screen updates at once.
    function drawFieldErrors(typingKey) {
      qa('[data-wpm-error]').forEach(box => {
        const key = box.dataset.wpmError;
        const html = touched.has(key) ? fieldErrorMarkup(fieldProblem(current, key)) : '';
        if (html && box.hidden && key === typingKey) {
          clearTimeout(errorTimers.get(key));
          errorTimers.set(key, setTimeout(() => showFieldError(key, fieldErrorMarkup(fieldProblem(current, key))), 900));
        } else showFieldError(key, html);
      });
    }

    // ---- The result ----
    function drawCode(result) {
      setHtml(codeEl, codeMarkup(result.text));
      if (!codeBox) return;
      // Measure folded: is anything cut off at the folded height?
      codeBox.dataset.open = 'false';
      codeBox.dataset.long = 'true';
      const long = codeEl.scrollHeight > codeEl.clientHeight + 4;
      if (!long) codeOpen = false;
      codeBox.dataset.long = String(long);
      codeBox.dataset.open = String(codeOpen);
      if (codeMore) {
        codeMore.hidden = !long;
        codeMore.setAttribute('aria-expanded', String(codeOpen));
        codeMore.textContent = codeOpen ? 'Show less' : result.layout === 'pretty' ? `Show all ${result.lines} lines` : 'Show all of it';
      }
    }

    function render({ announce = false, typingKey = '', fromUser = false } = {}) {
      const state = readState();
      current = validate(state);
      const { result, status } = current;
      const s = current.state;
      root.dataset.wpmMode = s.returnMode || 'unset';
      root.dataset.wpmAccepted = String(accepts(s));
      root.dataset.wpmFees = s.returnFees || 'unset';
      root.dataset.wpmShipping = s.shipping ? 'on' : 'off';
      root.dataset.wpmScope = s.shipScope;
      root.dataset.wpmRate = s.shipRate;
      root.dataset.wpmStatus = status;
      drawCode(result);
      if (statusLineEl) { setHtml(statusLineEl, statusMarkup(current)); statusLineEl.dataset.status = status; }
      if (meterEl) {
        meterEl.dataset.over = String(result.characters >= LIMIT);
        const fill = meterEl.firstElementChild;
        if (fill) fill.style.width = `${meterShare(result)}%`;
      }
      setHtml(readBackEl, readBackMarkup(s));
      drawChecks();
      drawFieldErrors(typingKey);
      const blocked = status === 'fix';
      copyButton.disabled = blocked;
      if (downloadButton) downloadButton.disabled = blocked;
      if (copyHintEl) copyHintEl.textContent = copyHint(current);
      qa('[data-wpm-currency-code]').forEach(el => { el.textContent = validCurrency(s.currency) ? s.currency : '—'; });
      // "Fill in the example" only when the form isn't the example; "Start
      // blank" only when it isn't blank. The tick box doesn't count.
      const same = target => FORM_KEYS.every(key => JSON.stringify(s[key]) === JSON.stringify(target[key]));
      const fillButton = q('[data-wpm-fill]');
      const clearButton = q('[data-wpm-clear]');
      if (fillButton) fillButton.hidden = same(SAMPLE_STATE);
      if (clearButton) clearButton.hidden = same(BLANK_STATE);
      if (announce && announcer) {
        clearTimeout(announceTimer);
        announceTimer = setTimeout(() => { announcer.textContent = summaryText(current); }, 900);
      }
      if (fromUser && !reportedBuilt && !blocked) { reportedBuilt = true; toolEvent('wix_policy_built'); }
    }

    function drawChecks() {
      const parts = checksMarkup(current);
      const key = `${parts.before}\u0000${parts.after}`;
      if (shown.get(checksEl) !== key) {
        shown.set(checksEl, key);
        Array.from(checksEl.children).forEach(row => { if (row !== matchRow) row.remove(); });
        const make = html => {
          const box = doc.createElement('ul');
          box.innerHTML = html;
          const fragment = doc.createDocumentFragment();
          Array.from(box.children).forEach(row => fragment.appendChild(row));
          return fragment;
        };
        if (matchRow) {
          checksEl.insertBefore(make(parts.before), matchRow);
          checksEl.appendChild(make(parts.after));
        } else {
          checksEl.appendChild(make(parts.before));
          checksEl.appendChild(make(parts.after));
        }
      }
      if (matchRow) {
        const check = current.checks.find(item => item.id === 'pages-match');
        matchRow.dataset.level = check.level;
        const word = matchRow.querySelector('[data-wpm-match-word]');
        if (word) word.textContent = LEVEL_WORDS[check.level];
      }
    }

    function changed(options = {}) {
      hideFallback();
      setStatus('');
      render({ announce: true, fromUser: true, ...options });
    }

    function hideFallback() { if (fallback) fallback.hidden = true; }

    async function copy() {
      if (!current || current.status === 'fix') return;
      const value = current.result.text;
      hideFallback();
      const done = 'Markup copied. Next, paste it into one page in Wix (steps below).';
      try {
        const clipboard = win.navigator && win.navigator.clipboard;
        if (!clipboard || typeof clipboard.writeText !== 'function') throw new Error('Clipboard unavailable');
        await clipboard.writeText(value);
        setStatus(done);
        toolEvent('wix_policy_copied');
      } catch (_) {
        // Some browsers block the Clipboard API (older Safari, in-app
        // browsers). Select the markup in a box and try the older command.
        if (!fallback || !fallbackText) { setStatus('Copy the markup from the box above.'); return; }
        fallbackText.value = value;
        fallback.hidden = false;
        fallbackText.focus();
        fallbackText.select();
        try { fallbackText.setSelectionRange(0, value.length); } catch (__) { /* not selectable */ }
        let copied = false;
        try { copied = typeof doc.execCommand === 'function' && doc.execCommand('copy'); } catch (__) { copied = false; }
        if (copied) {
          fallback.hidden = true;
          copyButton.focus();
          toolEvent('wix_policy_copied');
        }
        setStatus(copied ? done : 'Couldn’t copy by itself. The markup is selected in the box below, so copy it from there.');
      }
    }

    const downloads = new Map();
    function download() {
      if (!current || current.status === 'fix') return;
      const urls = win.URL;
      if (!urls || typeof urls.createObjectURL !== 'function' || typeof win.Blob !== 'function') { setStatus('Downloads don’t work here. Use Copy markup instead.'); return; }
      const url = urls.createObjectURL(new win.Blob([`${current.result.json}\n`], { type: 'application/json' }));
      const link = doc.createElement('a');
      link.href = url;
      link.download = 'return-shipping-policy.json';
      link.hidden = true;
      root.appendChild(link);
      link.click();
      link.remove();
      downloads.set(url, setTimeout(() => { urls.revokeObjectURL(url); downloads.delete(url); }, 1000));
      setStatus('Downloaded return-shipping-policy.json, the markup without script tags.');
    }

    function focusField(key) {
      const target = q(`[data-wpm-focus="${key}"]`) || fieldEl(key) || q(`input[name="wpm-${key}"]:checked`) || q(`input[name="wpm-${key}"]`);
      if (!target || typeof target.focus !== 'function') return;
      // The logo and profiles sit in a disclosure; open it to reach them.
      const folded = target.closest('details');
      if (folded && !folded.open) folded.open = true;
      target.focus();
    }

    // ---- Events ----
    root.addEventListener('input', event => {
      const target = event.target;
      const name = target.dataset && target.dataset.wpmField;
      if (!name || !TEXT_FIELDS.includes(name)) return;
      touched.add(errorKey(name));
      const typing = /^(insertText|deleteContent)/.test(event.inputType || '');
      changed({ typingKey: typing ? errorKey(name) : '' });
    });
    root.addEventListener('change', event => {
      const target = event.target;
      const name = (target.dataset && target.dataset.wpmField) || (target.name || '').replace(/^wpm-/, '');
      if (TEXT_FIELDS.includes(name)) { touched.add(errorKey(name)); drawFieldErrors(''); return; }
      if (name === 'editor' || !name) return;
      if (RADIO_FIELDS.includes(name) || BOX_FIELDS.includes(name) || FLAG_FIELDS.includes(name) || SELECT_FIELDS.includes(name)) {
        touched.add(name);
        changed();
      }
    });
    root.addEventListener('focusout', event => {
      const name = event.target.dataset && event.target.dataset.wpmField;
      if (name && TEXT_FIELDS.includes(name) && tidyText(event.target.value)) { touched.add(errorKey(name)); drawFieldErrors(''); }
    });
    root.addEventListener('click', event => {
      const action = event.target.closest('[data-wpm-action]');
      if (action && current) {
        const check = current.checks.find(item => item.id === action.dataset.wpmAction);
        if (!check || !check.action) return;
        writeState(check.action.patch);
        Object.keys(check.action.patch).forEach(key => touched.add(errorKey(key)));
        if (check.field) touched.add(check.field);
        changed();
        // The pressed button is gone now; keep keyboard users nearby.
        focusField(check.field || errorKey(Object.keys(check.action.patch)[0]));
        return;
      }
      const goto = event.target.closest('[data-wpm-goto]');
      if (goto) {
        const key = goto.dataset.wpmGoto;
        touched.add(key);
        drawFieldErrors('');
        focusField(key);
      }
    });
    copyButton.addEventListener('click', copy);
    if (downloadButton) downloadButton.addEventListener('click', download);
    if (codeMore) codeMore.addEventListener('click', () => {
      codeOpen = !codeOpen;
      drawCode(current.result);
      if (!codeOpen && codeBox && typeof codeBox.scrollIntoView === 'function' && codeBox.getBoundingClientRect().top < 0) codeBox.scrollIntoView({ block: 'nearest' });
    });
    const clear = q('[data-wpm-clear]');
    if (clear) clear.addEventListener('click', () => {
      const blank = {};
      POLICY_KEYS.forEach(key => { blank[key] = DEFAULTS[key]; });
      writeState(blank);
      touched.clear();
      codeOpen = false;
      changed();
      fieldEl('storeName').focus();
    });
    const fill = q('[data-wpm-fill]');
    if (fill) fill.addEventListener('click', () => {
      const sample = {};
      POLICY_KEYS.forEach(key => { sample[key] = SAMPLE[key]; });
      writeState(sample);
      touched.clear();
      // Bringing the example back isn't building markup of one's own.
      changed({ fromUser: false });
      fieldEl('storeName').focus();
    });
    if (win) win.addEventListener('pagehide', () => {
      clearTimeout(announceTimer);
      errorTimers.forEach(timer => clearTimeout(timer));
      downloads.forEach((timer, url) => { clearTimeout(timer); win.URL.revokeObjectURL(url); });
    });
    LIST_FIELDS.forEach(name => pickers[name] && pickers[name].draw());
    render();
  }

  return Object.freeze({
    META,
    DEFAULTS,
    SAMPLE,
    COUNTRIES,
    CURRENCIES,
    COMMON_CURRENCIES,
    LIMIT,
    PAGE_TOTAL,
    WIX_ADDING,
    WIX_TROUBLESHOOTING,
    PER_PAGE,
    MAX_RETURN_COUNTRIES,
    MAX_RETURN_DAYS,
    MAX_SHIP_DAYS,
    WORDS,
    RETURN_CATEGORIES,
    RETURN_FEES,
    RETURN_METHODS,
    REFUND_TYPES,
    ITEM_CONDITIONS,
    LEVEL_WORDS,
    OPEN_TAG,
    CLOSE_TAG,
    normalizeState,
    parseUrl,
    parseWholeNumber,
    parseAmount,
    parseMoney,
    currencyDecimals,
    countryList,
    countryName,
    searchCountries,
    parseCountryPaste,
    buildObject,
    buildMarkup,
    validate,
    readBack,
    statusLine,
    isLong,
    meterShare,
    codeMarkup,
    statusMarkup,
    readBackMarkup,
    checksMarkup,
    matchRowMarkup,
    fieldProblem,
    fieldErrorMarkup,
    copyHint,
    summaryText,
    chipsMarkup,
    matchesMarkup,
    isSample,
    directoryPreview,
    esc,
    init
  });
});
